import { formatDistanceToNowStrict } from 'date-fns';
import { FC, SVGProps, useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
// mui components
import { Avatar, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
// components
import DefaultAvatar from 'components/default-avatar';
import UploadedFiles from 'components/uploaded-files';
import { UploadedImagesWithStatus } from 'components/uploaded-images/uploaded-images-with-status';
import Post from '../../../post';
import Reply from '../../../reply';
import { CommentActionsPopover } from '../comment-actions-popover';
// custom hooks
import useToast from 'hooks/useToast';
// api
import { useGetMeQuery } from 'redux-store/api/auth';
import { useGetCompanyRolePermissionsMeQuery } from 'redux-store/api/company-roles';
// helpers
import { createContent, getFilenameFromUrl, getTimeSpend, isContentEmpty } from 'utils/helpers';
import { getMimeTypeFromFileName } from 'utils/helpers/fileCabinetHelpers';
import {
  ICommentUploads,
  PostVisibilityEnum,
  UploadStatusEnum,
} from 'redux-store/api/activity-feed';
import { useResolvedVideoThumbnails } from 'hooks/useResolvedVideoThumbnails';
// constants
// icons
import LikeIcon from 'assets/icons/buttons/like-icon.svg';
import ReplyIcon from 'assets/icons/buttons/reply-icon.svg';
import UnlikeIcon from 'assets/icons/buttons/unlike-icon.svg';
// styles
import { IPostComment } from '_interfaces/postComment.interface';
import { IProject } from '_interfaces/project';
import {
  deletePostCommentAtom,
  postCommentsAtom,
  togglePostCommentLikeAtom,
  updatePostCommentAtom,
} from 'atoms';
import { ICustomMentionData } from 'components/inputs/draft-js-input';
import { useAtomValue, useSetAtom } from 'jotai';
import { highlightSearchMatchesInHtml } from 'utils/searchHighlight';
import { RichTextContent } from 'widgets/side-panel/sections/activity-feed/old/components/rich-text-content';
import { commentItemStyles } from './styles';

interface ICommentItemProps {
  feedContextType?: 'ACTIVITY_FEED' | 'FILE_FEED' | 'SCHEDULE_FEED' | 'SELECTIONS_FEED';
  feedContextItemId?: string;
  commentAudience?: {
    postVisibility: PostVisibilityEnum;
    visibility: number[];
  };
  parentPath: string;
  comment: IPostComment;
  searchQuery: string;
  mentionOptions: ICustomMentionData[];
  project: IProject;
  createCommentActionsMenuItems: (
    onEdit: () => void,
    onDelete: () => void,
    commentUserId: number
  ) => {
    onClick: () => void;
    label: string;
    Icon: FC<
      SVGProps<SVGSVGElement> & {
        title?: string;
      }
    >;
  }[];
}

const useStyles = makeStyles((theme) => ({
  tooltip: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 8px',
    maxHeight: '99px',
    width: '104px',
    gap: '2px',
    background: '#23252F',
    boxShadow: '0px 4px 10px rgba(35, 37, 47, 0.1), 1px 2px 2px rgba(35, 37, 47, 0.04)',
    borderRadius: '8px',
  },
}));

const CommentItem: FC<ICommentItemProps> = ({
  parentPath,
  commentAudience,
  comment,
  searchQuery,
  mentionOptions,
  project,
  createCommentActionsMenuItems,
  feedContextType = 'ACTIVITY_FEED',
  feedContextItemId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const showToast = useToast();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [openReply, setOpenReply] = useState<boolean>(false);
  const replyRef = useRef<HTMLDivElement>(null);
  const toggleCommentLike = useSetAtom(togglePostCommentLikeAtom);
  const deleteComment = useSetAtom(deletePostCommentAtom);
  const updateComment = useSetAtom(updatePostCommentAtom);

  const { data: userMe } = useGetMeQuery();
  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();

  // Subscribe to real-time comment data to ensure UI updates when likes change
  const allComments = useAtomValue(postCommentsAtom(`${comment.parentPath}`)) || [];

  const realTimeComment = allComments.find((c) => c.id === comment.id) || comment;

  const handleOpenTextEditor = (open: boolean) => {
    setIsEditing(open);
  };

  const onEnableEditing = () => {
    setIsEditing(true);
  };

  const onDeleteComment = () => {
    try {
      deleteComment(realTimeComment.parentPath, realTimeComment.id);
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error deleting comment: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  };

  const onUpdateComment = async (updatedComment: IPostComment) => {
    try {
      await updateComment(updatedComment);
      setIsEditing(false);
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error updating comment: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  };

  const handleLikeUnlike = () => {
    toggleCommentLike({
      parentPath,
      userId: userMe?.id?.toString() || '',
      commentId: realTimeComment.id,
    });
  };

  const onReplyToggle = () => {
    setOpenReply((prev) => !prev);
  };

  const handleOpenReply = (open: boolean) => {
    setOpenReply(open);
  };

  const getCustomInputStyles = useCallback(
    (isError: boolean) => ({
      '.mnw6qvm': {
        borderRadius: '12px',
        marginTop: '12px',
        filter:
          'drop-shadow(0px 4px 10px rgba(35, 37, 47, 0.1)) drop-shadow(1px 2px 2px rgba(35, 37, 47, 0.04))',
        boxShadow: 'none',
      },
      '.DraftEditor-root': {
        height: '120px',
        minHeight: 'unset',
      },
      '.DraftEditor-editorContainer': {
        height: '120px',
        minHeight: 'unset',
        overflowY: 'auto',
        borderRadius: '8px',
        outline: isError ? '1px solid #d32f2f' : '1px solid #EDEFF7',
        fontFamily:
          "'Inter', 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', 'Twemoji', sans-serif",
        div: { boxSizing: 'border-box' },
        '&:hover': {
          outline: '1px solid #EDEFF7',
        },
        '&:focus-within': {
          outline: '1px solid #EDEFF7',
        },
      },
    }),
    []
  );

  const isUpdateCommentLoading = false;
  const isDeleteCommentLoading = false;

  if (isUpdateCommentLoading || isDeleteCommentLoading) {
    return (
      <Grid
        key={comment.id}
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '12px',
        }}
      >
        <CircularProgress />
      </Grid>
    );
  }

  const content = useMemo(() => {
    const baseContent = createContent(
      realTimeComment?.message ?? '',
      realTimeComment?.mentions ?? [],
      realTimeComment?.hashtags ?? [],
      userPermissions
    );
    return highlightSearchMatchesInHtml(baseContent, searchQuery);
  }, [
    realTimeComment.message,
    realTimeComment.mentions,
    realTimeComment.hashtags,
    searchQuery,
    userPermissions,
  ]);

  const { getThumbnail } = useResolvedVideoThumbnails(
    realTimeComment?.media,
    project?.id,
  );

  const commentUploads: ICommentUploads[] = useMemo(() => {
    return (realTimeComment?.uploads || []).map((upload) => {
      const isObject = typeof upload === 'object' && upload !== null;
      const url: string = isObject
        ? (upload as { url: string; name?: string }).url
        : String(upload);
      const name: string =
        isObject && (upload as { url: string; name?: string }).name
          ? (upload as { url: string; name?: string }).name!
          : getFilenameFromUrl(url);
      const mediaItem = realTimeComment?.media?.find((m) => m.url === url);
      const effectivePreviewUrl = mediaItem ? getThumbnail(mediaItem) : undefined;
      return {
        id: url,
        url: url,
        name: mediaItem?.name || name,
        mimeType: getMimeTypeFromFileName(name),
        description: mediaItem?.description ?? null,
        timestamp: mediaItem?.timestamp ?? null,
        location: mediaItem?.location ?? null,
        options: effectivePreviewUrl
          ? {
              soundLevel: mediaItem?.options?.soundLevel ?? [],
              duration: mediaItem?.options?.duration ?? '',
              previewUrl: effectivePreviewUrl,
            }
          : null,
        status: UploadStatusEnum.COMPLETED,
        fileCabinetId: mediaItem?.fileCabinetId,
      };
    });
  }, [realTimeComment?.uploads, realTimeComment?.media, getThumbnail]);

  const renderTextContent =
    content && !isContentEmpty(content) ? (
      <RichTextContent
        projectId={project?.id}
        content={content}
        sx={commentItemStyles.commentContent}
      />
    ) : null;

  const nameSplit = realTimeComment.createdBy?.displayName?.split(' ');
  const initials = `${nameSplit?.[0]?.[0]}${nameSplit?.[nameSplit?.length - 1]?.[0]}`;

  return (
    <Grid sx={commentItemStyles.container}>
      <Grid
        key={realTimeComment.id}
        sx={{ display: 'flex', width: '100%', flexDirection: 'row', gap: '12px' }}
      >
        <Grid sx={commentItemStyles.avatarContainer}>
          {realTimeComment.createdBy?.avatarUrl ? (
            <>
              {/* {!realTimeComment?.isView ? <Grid sx={commentItemStyles.greenDot}></Grid> : null} */}
              <Avatar
                alt="avatar"
                sx={commentItemStyles.customAvatar}
                src={realTimeComment.createdBy.avatarUrl}
                variant="square"
              />
            </>
          ) : (
            <>
              {/* {!realTimeComment?.isView ? <Grid sx={commentItemStyles.greenDot}></Grid> : null} */}
              <DefaultAvatar
                initials={initials}
                variant="square"
                alt="avatar"
                sx={{ borderRadius: '8px', width: '40px', height: '40px' }}
              />
            </>
          )}
        </Grid>
        {isEditing ? (
          <Post
            projectId={project.id}
            companyId={realTimeComment.companyId || ''}
            actionType="UPDATE_COMMENT"
            isExpanded={true}
            setOpen={handleOpenTextEditor}
            editableComment={realTimeComment}
            onUpdateComment={onUpdateComment}
            getCustomInputStyles={getCustomInputStyles}
            mentionOptions={mentionOptions}
            uploadPath={`companies/${realTimeComment.companyId}/projects/${project.id}/activityFeed`}
            feedContextType={feedContextType}
            feedContextItemId={feedContextItemId}
          />
        ) : (
          <Grid
            sx={{
              display: 'flex',
              width: 'calc(100% - 40px - 12px)',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Grid sx={commentItemStyles.commentContainer}>
              <Grid sx={commentItemStyles.commentInfoContainer}>
                <Grid sx={commentItemStyles.authorNameContainer}>
                  <Typography
                    sx={commentItemStyles.authorName}
                  >{`${realTimeComment.createdBy.displayName}`}</Typography>
                  <Grid
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      flexDirection: 'row',
                      gap: '8px',
                    }}
                  >
                    <Typography sx={commentItemStyles.timeSpend}>
                      {getTimeSpend(formatDistanceToNowStrict(new Date(realTimeComment.createdAt)))}
                    </Typography>
                    {(userPermissions?.ACTIVITY_FEED_ALL_POSTS?.canDelete &&
                      userPermissions?.ENABLE_ACTIVITY_FEED?.canDelete) ||
                    userMe?.userId?.toString() === realTimeComment?.createdBy?.userId ? (
                      <CommentActionsPopover
                        items={createCommentActionsMenuItems(
                          onEnableEditing,
                          onDeleteComment,
                          Number(realTimeComment?.createdBy?.userId)
                        )}
                      />
                    ) : null}
                  </Grid>
                </Grid>
                {/* <Typography sx={commentItemStyles.authorRole}>{realTimeComment.profile.role}</Typography> */}
              </Grid>
              {renderTextContent}

              <UploadedImagesWithStatus
                type="activity_feed"
                containerStyles={commentItemStyles.imagesContainer}
                imageStyles={commentItemStyles.image}
                uploads={commentUploads}
                visibleImagesCount={5}
              />
              <UploadedFiles type="activity_feed" uploads={commentUploads} />
            </Grid>
            <Grid sx={commentItemStyles.actionsContainer}>
              <Grid sx={commentItemStyles.likeReplyContainer}>
                <Button
                  size="small"
                  startIcon={
                    realTimeComment?.likes?.includes(userMe?.id?.toString() || '') ? (
                      <UnlikeIcon />
                    ) : (
                      <LikeIcon />
                    )
                  }
                  sx={commentItemStyles.actionBtn}
                  onClick={handleLikeUnlike}
                >
                  {realTimeComment?.likes?.includes(userMe?.id?.toString() || '')
                    ? 'Liked'
                    : 'Like'}
                </Button>
                <Button
                  size="small"
                  startIcon={<ReplyIcon />}
                  sx={commentItemStyles.actionBtn}
                  onClick={onReplyToggle}
                >
                  Reply
                </Button>
              </Grid>
              <Grid sx={commentItemStyles.postInfoContainer}>
                {!realTimeComment?.likes?.length ? (
                  <Button size="small" sx={commentItemStyles.postInfoBtn}>
                    <Typography sx={commentItemStyles.withoutUnderline}>{`${0} like`}</Typography>
                  </Button>
                ) : (
                  // <Tooltip
                  //   classes={classes}
                  //   placement={'bottom-start'}
                  //   onOpen={() => {
                  //     if (isUninitialized) {
                  //       getLikers({
                  //         limit: 4,
                  //         activityFeedId: realTimeComment.activityFeedItemId || realTimeComment.id,
                  //       });
                  //     }
                  //   }}
                  //   title={
                  //     <>
                  //       {isLikersLoading || isLikersFetching ? (
                  //         <CircularProgress sx={commentItemStyles.circularProgress} size={20} />
                  //       ) : (
                  //         likersData?.items?.map((item) => {
                  //           return (
                  //             <Typography sx={commentItemStyles.likeInfo} key={item.id}>
                  //               {`${item.profile.firstName} ${item.profile.lastName}`}
                  //             </Typography>
                  //           );
                  //         })
                  //       )}
                  //       <Typography sx={commentItemStyles.likeInfo}>
                  //         {realTimeComment.likesCount > 4 && `and ${realTimeComment.likesCount - 4} more`}
                  //       </Typography>
                  //     </>
                  //   }
                  // >
                  <Button
                    size="small"
                    sx={commentItemStyles.postInfoBtn}
                    onClick={() => {
                      searchParams.set('likesCommentId', realTimeComment.id);
                      searchParams.set('likesCommentParentPath', realTimeComment.parentPath);
                      setSearchParams(searchParams);
                    }}
                  >
                    <Typography sx={commentItemStyles.hoverUnderline}>
                      {realTimeComment?.likes?.length === 1
                        ? `${1} like `
                        : `${realTimeComment?.likes?.length} likes`}
                    </Typography>
                  </Button>
                  // </Tooltip>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
      {openReply ? (
        <Reply
          commentAudience={commentAudience}
          companyId={realTimeComment.companyId}
          projectId={project.id}
          setOpen={handleOpenReply}
          comment={realTimeComment}
          replyRef={replyRef}
          mentionOptions={mentionOptions}
          uploadPath={`companies/${realTimeComment.companyId}/projects/${project.id}/activityFeed`}
          parentPostId={
            realTimeComment.postId ||
            (realTimeComment.parentPath || '').split('/').pop() ||
            ''
          }
          feedContextType={feedContextType}
          feedContextItemId={feedContextItemId}
        />
      ) : null}
    </Grid>
  );
};

export default CommentItem;
